/**
 * The controlled vocabularies for a hero slide's visual attributes.
 *
 * WHY TOKENS AND NOT RAW TAILWIND CLASSES
 * ---------------------------------------
 * The hero slides live in the database (`cms_hero_slides.gradient_token`,
 * `.image_position`), and an operator picks those values from a dropdown in
 * the admin. They are stored as SHORT TOKENS (`green`, `center`) and mapped to
 * real utility classes HERE, in a file that is tracked by git.
 *
 * The alternative — storing `from-yada-green/90 via-yada-green/70
 * to-yada-olive/60` in the row — looks harmless and is quietly broken.
 * Tailwind v4 has no `safelist` and no config file: it emits CSS only for the
 * class names it finds by SCANNING THE SOURCE TREE. A class string that
 * lives in a MySQL row is invisible to the scanner, so `from-yada-green/90`
 * would never be emitted and the slide would render with NO GRADIENT AT ALL —
 * a transparent card, no error, no warning, and no test failure either. The
 * same trap applies to the generated snapshot files: `src/content/cms/` is
 * gitignored, and Tailwind skips gitignored paths, so a class string in
 * `hero-slides.generated.ts` would be just as invisible.
 *
 * Putting the classes here fixes both ends at once: the strings are in a
 * tracked `.ts` file, so the scanner sees them, and the database only ever
 * holds values from a closed set that a dropdown offers.
 *
 * `scripts/test-hero-tokens.ts` locks the contract in: every token resolves
 * to a class, every class in this file is reachable from a token (no dead
 * entries), and this file is tracked by git (if it were ever gitignored the
 * classes would vanish from the build and the slides would lose their
 * gradients again — the same failure, one layer up).
 *
 * The `object-position` classes are plain Tailwind utilities, but they are
 * tokenised for the same reason: the set is closed and the admin UI can
 * offer it as a dropdown instead of a free-text field that would let an
 * operator type a class that was never emitted.
 */

/** The closed set of gradient presets a slide may use. */
export type HeroGradientToken =
  | 'green'
  | 'olive'
  | 'gold'
  | 'coffee'
  | 'brown'
  | 'greenDark'

/** The closed set of `object-position` presets a slide's photo may use. */
export type HeroImagePositionToken =
  | 'center'
  | 'left'
  | 'right'
  | 'top'
  | 'bottom'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'

/**
 * Gradient token → the `from-* via-* to-*` utilities for the slide's card.
 *
 * These are the six gradients the hard-coded `HeroSlider` shipped with, in
 * the same order, so the seeded slides look exactly like the site did before
 * the CMS took over.
 */
export const HERO_GRADIENT_CLASSES: Record<HeroGradientToken, string> = {
  green: 'from-yada-green/90 via-yada-green/70 to-yada-olive/60',
  olive: 'from-yada-olive/90 via-yada-brown/70 to-yada-coffee/60',
  gold: 'from-yada-gold/90 via-yada-gold/70 to-yada-olive/60',
  coffee: 'from-yada-coffee/90 via-yada-coffee/70 to-yada-brown/60',
  brown: 'from-yada-brown/90 via-yada-olive/70 to-yada-green/60',
  greenDark: 'from-yada-green-dark/90 via-yada-green/70 to-yada-gold-soft/60',
}

/** `object-position` token → the Tailwind utility that sets it. */
export const HERO_IMAGE_POSITION_CLASSES: Record<HeroImagePositionToken, string> = {
  center: 'object-center',
  left: 'object-left',
  right: 'object-right',
  top: 'object-top',
  bottom: 'object-bottom',
  'top-left': 'object-top-left',
  'top-right': 'object-top-right',
  'bottom-left': 'object-bottom-left',
  'bottom-right': 'object-bottom-right',
}

/** The gradient used when a row's token is missing or unrecognised. */
export const DEFAULT_HERO_GRADIENT: HeroGradientToken = 'green'

/** The `object-position` used when a row's token is missing or unrecognised. */
export const DEFAULT_HERO_IMAGE_POSITION: HeroImagePositionToken = 'center'

/** Every gradient token, in the order the admin dropdown offers them. */
export const HERO_GRADIENT_TOKENS = Object.keys(HERO_GRADIENT_CLASSES) as HeroGradientToken[]

/** Every `object-position` token, in the order the admin dropdown offers them. */
export const HERO_IMAGE_POSITION_TOKENS = Object.keys(
  HERO_IMAGE_POSITION_CLASSES,
) as HeroImagePositionToken[]

/**
 * The gradient classes for a token read out of the database.
 *
 * A row can hold a token this build does not know about — an operator on a
 * newer admin, or a hand-edited row. Returning the default keeps the slide
 * rendered instead of emitting the literal string `undefined` into a
 * `className`, which would leak into the DOM as a bogus class and leave the
 * card with no background at all.
 */
export function heroGradientClass(token: string | null | undefined): string {
  if (token && token in HERO_GRADIENT_CLASSES) {
    return HERO_GRADIENT_CLASSES[token as HeroGradientToken]
  }
  return HERO_GRADIENT_CLASSES[DEFAULT_HERO_GRADIENT]
}

/**
 * The `object-position` class for a token read out of the database, with the
 * same unknown-token fallback as {@link heroGradientClass}.
 */
export function heroImagePositionClass(token: string | null | undefined): string {
  if (token && token in HERO_IMAGE_POSITION_CLASSES) {
    return HERO_IMAGE_POSITION_CLASSES[token as HeroImagePositionToken]
  }
  return HERO_IMAGE_POSITION_CLASSES[DEFAULT_HERO_IMAGE_POSITION]
}
